import { View, Text } from '@24i/nxg-sdk-quarks';
import React, { FC, useEffect, useState } from 'react';
import {
    getTelegramWebAppTopInset,
    getTelegramWebAppTopFullScreen,
    isTelegramKVersion,
    isTelegramAVersion,
    isWebMobile,
    getTelegramWebApp,
    initializeTelegramBackButton,
} from '@24i/nxg-sdk-higgs/src/utils';
import { useTheme } from '@24i/nxg-sdk-higgs';
import BackButton from '@24i/nxg-sdk-gluons/src/components/buttons/BackButton';
import { useDebounceFn } from '@24i/nxg-core-utils/src';

import { I18nManager } from 'react-native';
import getStyles from '../withWebHeader/styles';
import { styles } from './styles';

const THRESHOLD_FOR_HEADER_BACKGROUND = 200;

type Props = {
    onBack: () => void;
    title: string;
    iconSize?: number;
    style?: object;
};

export const StickyBackHeader: FC<Props> = ({ onBack, title, children, iconSize, style }) => {
    const [shouldShowBackground, setShouldShowBackground] = useState(false);

    const { theme } = useTheme();
    const withWebHeaderStyles = getStyles(theme, false);
    const stickyStyles = styles();
    const isTelegramK = isTelegramKVersion();
    const isTelegramA = isTelegramAVersion();
    const isTelegram = isTelegramK || isTelegramA;
    const isMobileWeb = isWebMobile();
    const TelegramWebApp = getTelegramWebApp();
    const isFullscreen = TelegramWebApp?.isFullscreen === true;

    const textColor = shouldShowBackground ? theme.color.textSecondary : theme.color.textPrimary;
    const backIconName = shouldShowBackground ? 'onDark-back' : 'onPrimary-back';

    // Properly calculate padding top based on Telegram version and fullscreen state
    // For Telegram A non-fullscreen, ALWAYS use 0 padding to fix the weird spacing
    // This is critical for ALL screen widths, especially mobile breakpoint with bottom tab bar
    let paddingTop = 0;
    if (isTelegram && !(isTelegramA && !isFullscreen)) {
        // Use normal calculation for Telegram versions/states except Telegram A non-fullscreen
        paddingTop = getTelegramWebAppTopFullScreen() + getTelegramWebAppTopInset();
    }

    const handleBackroundVisibility = (distanceFromTop: number) => {
        if (distanceFromTop >= THRESHOLD_FOR_HEADER_BACKGROUND) {
            setShouldShowBackground(true);
        } else {
            setShouldShowBackground(false);
        }
    };

    const { run: debouncedHandleScroll } = useDebounceFn(
        (distanceFromTop: number) => {
            handleBackroundVisibility(distanceFromTop);
        },
        { wait: 200 }
    );

    useEffect(() => {
        const onScroll = () => {
            const scrolledPixels = window.scrollY;
            debouncedHandleScroll(scrolledPixels);
        };

        window.addEventListener('scroll', onScroll);

        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [debouncedHandleScroll]);

    // Handle Telegram back button initialization if applicable
    useEffect(() => {
        // Only apply to Telegram environments
        if (!isTelegram || !TelegramWebApp || !onBack) {
            return () => {};
        }

        // IMPORTANT: StickyBackHeader should never be shown for Telegram
        // This is a fallback in case withWebHeader's logic is bypassed
        // Always use the native Telegram back button
        const useNativeButton = true;

        const cleanup = initializeTelegramBackButton(
            TelegramWebApp,
            onBack,
            isTelegramK,
            useNativeButton
        );

        return typeof cleanup === 'function' ? cleanup : () => {};
    }, [TelegramWebApp, isTelegram, isTelegramK, isTelegramA, isFullscreen, onBack]);

    // Enhance back button appearance for any Telegram version or mobile web
    // This is critical since the native back button may be problematic
    const buttonStyles =
        isTelegram || isMobileWeb
            ? {
                  ...withWebHeaderStyles.backButtonIcon,
                  opacity: 1, // Ensure it's fully visible
                  fontSize: (iconSize || 16) * 1.25, // Make it 25% larger for better visibility
                  padding: 8, // Add some padding to increase tap target
                  color: textColor, // Ensure proper color contrast
                  cursor: 'pointer', // Show pointer cursor on hover
              }
            : withWebHeaderStyles.backButtonIcon;

    return (
        <>
            <View
                style={{
                    ...stickyStyles.wrapper,
                    ...style,
                    backgroundColor: shouldShowBackground
                        ? theme.color.darkerBlur1.color
                        : 'transparent',
                    borderBottomWidth: shouldShowBackground ? 1 : 0,
                }}
            >
                <View style={{ ...stickyStyles.textContainer, paddingTop }}>
                    <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center' }}>
                        <BackButton
                            onPress={onBack}
                            iconName={`${backIconName}${I18nManager.isRTL ? '_rtl' : ''}`}
                            iconSize={iconSize || 16}
                            color={textColor}
                            additionalIconStyles={buttonStyles}
                        />
                        {title && (
                            <Text
                                style={{
                                    ...withWebHeaderStyles.headerText,
                                    color: textColor,
                                    opacity: 1,
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    flex: 1,
                                    marginRight: 30, // Balance the back button width
                                }}
                                numberOfLines={1}
                            >
                                {title}
                            </Text>
                        )}
                    </View>
                </View>
            </View>
            {children}
        </>
    );
};
